class PromotionalDrawer extends HTMLElement {
  constructor() {
    super();

    this.cart = document.querySelector('cart-drawer');
    this.lineItemStatusElement = this.cart?.querySelector('#CartDrawer-LineItemStatus');
    this.mainCart = document.getElementById('main-cart-items');
    this.onScrollHandler = this.handleScroll.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#PromotionalDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    this.setDrawerOpeningLinks();
    this.bindScrollHandler();

    this.drawerContent = this.querySelector('.drawer__content');
  }

  handleScroll(event) {
    const panel = event.target;
    if (panel.scrollHeight - panel.scrollTop !== panel.clientHeight) return;

    fetch(`${this.dataset.url}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const promotionalBanner = html.getElementById(this.id);
        const productGrid = promotionalBanner?.querySelector('.product-grid');

        if (!productGrid || !this.drawerContent) return;

        const currentGrid = this.drawerContent.querySelector('.product-grid');
        if (!currentGrid) return;

        productGrid.querySelectorAll('.grid__item').forEach((item) => {
          if (!currentGrid.querySelector(`[data-product="${item.dataset.product}"]`)) {
            currentGrid.appendChild(item);
          }
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  bindScrollHandler() {
    if (!this.drawerContent) return;
    this.drawerContent.removeEventListener('scroll', this.onScrollHandler);
    this.drawerContent.addEventListener('scroll', this.onScrollHandler);
  }

  connectedCallback() {
    if (typeof subscribe !== 'function') return;

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.setDrawerOpeningLinks();
      this.bindScrollHandler();
      this.sortDrawerItems();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    this.drawerContent?.removeEventListener('scroll', this.onScrollHandler);
  }

  async getCart() {
    return fetch('/cart.js').then((response) => response.json());
  }

  setLinksUpdatePromos() {
    this.setDrawerOpeningLinks();
    this.sortDrawerItems();
  }

  async sortDrawerItems(cartData) {
    const cart = cartData?.items ? cartData : await this.getCart();
    if (!cart?.items?.length) return;

    const drawerItems = this.querySelectorAll('.grid__item');
    drawerItems.forEach((item) => {
      item.style.order = null;

      const title = item.dataset.product;
      if (!title) return;

      const animalName = title.split(' ')[0].replace("'s", '').replace('’s', '');
      const cartItem = cart.items.filter((entry) => entry.product_title.includes(animalName));
      if (cartItem.length > 0) item.style.order = '-1';
    });
  }

  enableLoading(lines) {
    const mainCartItems = this.cart?.querySelector('#CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    lines.forEach((line) => {
      const cartDrawerItemElements = this.cart?.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
      [...(cartDrawerItemElements || [])].forEach((overlay) => overlay.classList.remove('hidden'));
    });

    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(lines) {
    const mainCartItems = this.cart?.querySelector('#CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    lines.forEach((line) => {
      const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
      cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    });
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  setDrawerOpeningLinks() {
    const data = JSON.parse(this.dataset.data || '{}');
    const promoLinks = document.querySelectorAll(`.promo-link-${this.dataset.promoId}, .promo-link-cart`);

    promoLinks.forEach((promoLink) => {
      if (promoLink.classList.contains('active-listener')) return;

      promoLink.classList.add('active-listener');
      promoLink.setAttribute('role', 'button');
      promoLink.setAttribute('aria-haspopup', 'dialog');
      promoLink.addEventListener('click', (event) => {
        event.preventDefault();
        const parent = event.target.closest('cart-drawer') || event.target.closest('promotional-drawer');
        if (parent) parent.close();

        if (event.target.classList.contains('promo-link-cart')) {
          document.querySelector('cart-drawer')?.open();
        } else {
          this.open(promoLink);
        }
      });
    });

    if (data.additional_modal_links) {
      const additionalModalLinks = document.querySelectorAll(`.${data.additional_modal_links}`);

      additionalModalLinks.forEach((promoLink) => {
        if (promoLink.classList.contains('active-listener')) return;

        promoLink.classList.add('active-listener');
        promoLink.setAttribute('role', 'button');
        promoLink.setAttribute('aria-haspopup', 'dialog');
        promoLink.addEventListener('click', (event) => {
          event.preventDefault();
          const parent = event.target.closest('cart-drawer') || event.target.closest('promotional-drawer');
          if (parent) parent.close();
          this.open(promoLink);
        });
      });
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('promotional-drawer', PromotionalDrawer);
